import React, {useCallback, useMemo, useState} from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {Modal} from 'components/ui-kit/Modal';
import {$doesInvoiceExist} from 'models/documents/invoices/common/init';
import {
    $invoiceLinesHolder,
    removeInvoiceLinesHolder,
    setInvoiceLinesHolder,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';
import {
    $modalGroupByGtinSettings,
    setModalGroupByGtinSettings,
} from 'models/documents/invoices/details';

import {Footer, Headline, List, ListItem, Root, SelectedCount} from './styled';

export const ModalGroupByGtin = () => {
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.grouping';
    const t = (key: string) => translation(`${GEN_INFO}.${key}`);
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const modalGroupByGtinSettings = useStore($modalGroupByGtinSettings);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const onCloseModal = () => {
        setModalGroupByGtinSettings({lineIndex: -1, show: false, gtin: undefined});
    };

    const options = useMemo(() => {
        if (!modalGroupByGtinSettings.show) {
            return [];
        }

        const combinedCodes = invoiceLinesHolder[modalGroupByGtinSettings.lineIndex];
        return (
            combinedCodes?.combinedCodes?.map((i, index) => ({
                label: i,
                value: i,
            })) ?? []
        );
    }, [invoiceLinesHolder, modalGroupByGtinSettings.lineIndex, modalGroupByGtinSettings.show]);

    const onDelete = useCallback(() => {
        const item = invoiceLinesHolder[modalGroupByGtinSettings.lineIndex];
        const {combinedCodes, combinedCodeId, ...rest} = item;
        const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
        if (combinedCodes) {
            const updatedCodes = combinedCodes.filter((code) => !selectedValues.includes(code));

            const addCharges = rest['charges']
                ? toLowerAndTrim(rest['charges'].isCharge) === 'true'
                    ? Number(rest.charges.amountXaf)
                    : -Number(rest.charges.amountXaf)
                : 0;

            const qty = updatedCodes.length;
            const lines = (qty: number) => qty * Number(rest['price']) + addCharges;
            const lineAmountWVat = (qty: number) =>
                (Number(rest.vat) / 100) * lines(qty) + lines(qty);

            if (updatedCodes.length < 2) {
                removeInvoiceLinesHolder(modalGroupByGtinSettings.lineIndex);

                updatedCodes.length === 1 &&
                    setInvoiceLinesHolder([
                        {
                            ...rest,
                            code: updatedCodes[0],
                            qty: '1',
                            lines: !rest['price'] || !qty ? '' : lines(1).toFixed(2),
                            lineAmountWVat:
                                !rest['price'] || !qty ? '' : lineAmountWVat(1).toFixed(2),
                        },
                    ]);
            } else {
                updateInvoiceLinesHolder({
                    index: modalGroupByGtinSettings.lineIndex,
                    data: [
                        {
                            columnId: 'combinedCodes',
                            value: updatedCodes,
                        },
                        {
                            columnId: 'qty',
                            value: qty.toString(),
                        },
                        {
                            columnId: 'lines',
                            value: !rest['price'] || !qty ? '' : lines(qty).toFixed(2),
                        },
                        {
                            columnId: 'lineAmountWVat',
                            value: !rest['price'] || !qty ? '' : lineAmountWVat(qty).toFixed(2),
                        },
                    ],
                });
            }

            // const mappedSelectedValues = selectedValues.map((code) => ({
            //     ...rest,
            //     code,
            //     qty: '1',
            //     lines: lines ? lines(1).toFixed(2) : '',
            //     lineAmountWVat: lineAmountWVat ? lineAmountWVat(1).toFixed(2) : '',
            // }));
            // setInvoiceLinesHolder(mappedSelectedValues);

            setSelectedValues([]);

            if (updatedCodes.length < 2) {
                onCloseModal();
            }
            // else {
            //     setModalGroupByGtinSettings({
            //         ...modalGroupByGtinSettings,
            //         lineIndex: modalGroupByGtinSettings.lineIndex + mappedSelectedValues.length,
            //     });
            // }
        }
    }, [invoiceLinesHolder, modalGroupByGtinSettings.lineIndex, selectedValues]);

    return (
        <Modal
            show={modalGroupByGtinSettings.show}
            onClose={onCloseModal}
            title="Consumer codes"
            modalWidth={0}
        >
            <Root>
                {!doesInvoiceExist && <Headline>{t('deleteFromGroup')}</Headline>}
                {doesInvoiceExist ? (
                    <List>
                        {options.map((option) => (
                            <ListItem key={option.value}>{option.label}</ListItem>
                        ))}
                    </List>
                ) : (
                    <CheckboxArray
                        searchable={true}
                        name="combinedCodes"
                        options={options}
                        onChange={(values) => setSelectedValues(values ?? [])}
                    />
                )}
                {!doesInvoiceExist && (
                    <Footer>
                        <SelectedCount>
                            {selectedValues.length} {t('selected')}
                        </SelectedCount>
                        <Button
                            type="button"
                            style={{width: 'fit-content', color: 'red', borderColor: 'red'}}
                            size="md"
                            variant={ButtonVariant.Outline}
                            onClick={onDelete}
                        >
                            {t('delete')}
                        </Button>
                    </Footer>
                )}
            </Root>
        </Modal>
    );
};
